/**
 * Parametric EQ fitting via greedy peak-picking.
 *
 * Given a measured response and a target curve, this module computes the
 * error curve and iteratively places peaking biquad filters to minimize it.
 *
 * Algorithm:
 * 1. Compute error = target - measured (dB)
 * 2. Find the frequency with the largest |error|
 * 3. Determine Q from the local shape of the error around that peak
 * 4. Place a peaking filter (fc, gain, Q)
 * 5. Subtract the filter's effect from the error
 * 6. Repeat until N bands or max |error| < threshold
 */

import type { EqBand, EqPreset, OctaveBand } from "../types";
import {
  peakingMagnitudeDb,
  lowShelfMagnitudeDb,
  highShelfMagnitudeDb,
  peakGain,
} from "./biquad";

/** Constraints for EQ fitting */
interface FitOptions {
  /** Maximum number of bands to fit */
  maxBands: number;
  /** Minimum absolute error (dB) to place a new band */
  minError: number;
  /** Minimum allowed Q */
  minQ: number;
  /** Maximum allowed Q */
  maxQ: number;
  /** Maximum allowed gain magnitude */
  maxGain: number;
}

const DEFAULT_FIT_OPTIONS: FitOptions = {
  maxBands: 10,
  minError: 0.5,
  minQ: 0.5,
  maxQ: 6.0,
  maxGain: 12.0,
};

/**
 * Estimate Q from the local bandwidth of an error peak.
 *
 * Looks at the error curve around the peak and finds the points
 * where the error drops to half (-3 dB equivalent in the error domain).
 * Q = fc / bandwidth.
 */
function estimateQ(
  error: Float64Array,
  bands: OctaveBand[],
  peakIdx: number,
  minQ: number,
  maxQ: number
): number {
  const peakErr = Math.abs(error[peakIdx]);
  const halfPeak = peakErr / 2;
  const fc = bands[peakIdx].center;

  // Search left for half-power point
  let leftFreq = bands[0].center;
  for (let i = peakIdx - 1; i >= 0; i--) {
    const errNext = Math.abs(error[i + 1]);
    const errCurr = Math.abs(error[i]);
    if (errCurr < halfPeak) {
      // Interpolate, guarding against division by zero
      const delta = errNext - errCurr;
      const ratio = delta > 0 ? (halfPeak - errCurr) / delta : 0.5;
      leftFreq =
        bands[i].center *
        Math.pow(bands[i + 1].center / bands[i].center, ratio);
      break;
    }
  }

  // Search right for half-power point
  let rightFreq = bands[bands.length - 1].center;
  for (let i = peakIdx + 1; i < bands.length; i++) {
    const errPrev = Math.abs(error[i - 1]);
    const errCurr = Math.abs(error[i]);
    if (errCurr < halfPeak) {
      const delta = errPrev - errCurr;
      const ratio = delta > 0 ? (halfPeak - errCurr) / delta : 0.5;
      rightFreq =
        bands[i].center *
        Math.pow(bands[i - 1].center / bands[i].center, ratio);
      break;
    }
  }

  const bandwidth = rightFreq - leftFreq;
  if (!Number.isFinite(bandwidth) || bandwidth <= 0) return maxQ;

  const q = fc / bandwidth;
  return Math.max(minQ, Math.min(maxQ, q));
}

/**
 * Decide whether a broad error near the spectrum edge is better served by a
 * shelf filter (LSC / HSC) or a peaking filter (PK).
 *
 * Shelves are used for broad corrections at the spectrum edges where the
 * error maintains the same sign across many adjacent bands.  For edge peaks
 * we measure the "span" of significant error rather than relying on estimated
 * Q, because the half-power bandwidth calculation is truncated at the spectrum
 * boundary and artificially inflates Q.
 *
 *   - LSC when the peak is within the first few bands and the error spans
 *     many bands toward higher frequencies.
 *   - HSC when the peak is within the last few bands and the error spans
 *     many bands toward lower frequencies.
 *
 * Narrow resonances / room modes stay as PK.
 */
function chooseFilterType(
  error: Float64Array,
  peakIdx: number,
  bands: OctaveBand[],
  estimatedQ: number
): "PK" | "LSC" | "HSC" {
  if (Math.abs(error[peakIdx]) < 0.5) return "PK";

  const isBassEdge = peakIdx <= 2;
  const isTrebleEdge = peakIdx >= bands.length - 3;

  if (!isBassEdge && !isTrebleEdge) return "PK";

  // For edge peaks, determine if the error is broad by counting how many
  // adjacent bands share the same sign and stay above a threshold.
  const sign = Math.sign(error[peakIdx]);
  const threshold = Math.abs(error[peakIdx]) * 0.3;

  if (isBassEdge) {
    let span = 0;
    for (let i = peakIdx; i < bands.length; i++) {
      if (Math.sign(error[i]) === sign && Math.abs(error[i]) >= threshold) {
        span++;
      } else {
        break;
      }
    }
    // A span of 4+ bands (~half an octave or more) indicates a shelf;
    // an extremely low estimated Q (< 0.7) is also clearly a shelf.
    if (span >= 4 || estimatedQ < 0.7) return "LSC";
  }

  if (isTrebleEdge) {
    let span = 0;
    for (let i = peakIdx; i >= 0; i--) {
      if (Math.sign(error[i]) === sign && Math.abs(error[i]) >= threshold) {
        span++;
      } else {
        break;
      }
    }
    if (span >= 4 || estimatedQ < 0.7) return "HSC";
  }

  return "PK";
}

/**
 * Fit parametric EQ bands to correct a measured response toward a target.
 *
 * @param measured - measured response in dB per band
 * @param target - target response in dB per band (inverse C-weighting)
 * @param bands - the octave band definitions
 * @param options - fitting constraints
 * @returns EqPreset ready for APO export
 */
export function fitEq(
  measured: Float64Array,
  target: Float64Array,
  bands: OctaveBand[],
  options: Partial<FitOptions> = {}
): EqPreset {
  const opts = { ...DEFAULT_FIT_OPTIONS, ...options };

  // Compute initial error, ignoring non-finite data (e.g. -Infinity from
  // bands with no FFT bins or no signal energy). Those bands should not
  // drive the fitting process.
  const error = new Float64Array(measured.length);
  const valid = new Array<boolean>(measured.length);
  for (let i = 0; i < measured.length; i++) {
    valid[i] = Number.isFinite(measured[i]) && Number.isFinite(target[i]);
    error[i] = valid[i] ? target[i] - measured[i] : 0;
  }

  const eqBands: EqBand[] = [];

  for (let n = 0; n < opts.maxBands; n++) {
    // Find the band with the largest absolute error (only among valid bands)
    let maxAbsErr = 0;
    let maxIdx = -1;
    for (let i = 0; i < error.length; i++) {
      if (!valid[i]) continue;
      const absErr = Math.abs(error[i]);
      if (absErr > maxAbsErr) {
        maxAbsErr = absErr;
        maxIdx = i;
      }
    }

    // Stop if no valid band exceeds the error threshold
    if (maxIdx < 0 || maxAbsErr < opts.minError) break;

    const fc = bands[maxIdx].center;
    const gain = Math.max(-opts.maxGain, Math.min(opts.maxGain, error[maxIdx]));

    // Estimate Q first so the shelf heuristic can use it
    const estimatedQ = estimateQ(error, bands, maxIdx, opts.minQ, opts.maxQ);
    const type = chooseFilterType(error, maxIdx, bands, estimatedQ);

    const q = type === "PK" ? estimatedQ : 0.71; // Butterworth Q for shelves

    const band: EqBand = {
      frequency: fc,
      gain,
      q,
      type,
      enabled: true,
    };

    eqBands.push(band);

    // Subtract this filter's effect from the error (only for valid bands)
    for (let i = 0; i < error.length; i++) {
      if (!valid[i]) continue;
      if (type === "PK") {
        error[i] -= peakingMagnitudeDb(bands[i].center, fc, gain, q);
      } else if (type === "LSC") {
        error[i] -= lowShelfMagnitudeDb(bands[i].center, fc, gain, q);
      } else {
        error[i] -= highShelfMagnitudeDb(bands[i].center, fc, gain, q);
      }
    }
  }

  // Apply a modest preamp to prevent clipping, but cap it so the overall
  // level never drops more than 6 dB.  We allow up to 6 dB of net boost
  // before attenuating — enough for moderate corrections without making
  // the speaker “much quieter”.
  const peak = peakGain(eqBands);
  const rawPreamp = Math.max(0, peak - 6);
  const preamp = rawPreamp === 0 ? 0 : Math.max(-6, -rawPreamp);

  // Round values for cleaner output
  for (const band of eqBands) {
    band.frequency = Math.round(band.frequency * 10) / 10;
    band.gain = Math.round(band.gain * 100) / 100;
    band.q = Math.round(band.q * 100) / 100;
  }

  return {
    preamp: Math.round(preamp * 100) / 100,
    bands: eqBands,
  };
}
